if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.section = {
          settings: JSON.parse(this.getAttribute('data-section-settings') || '{}')
        };
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
          dots: this.querySelector('.mobile-dot-navigation'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        this.mobileMql = window.matchMedia('(max-width: 749px)');
        this.productInfo = document.getElementById(`ProductInfo-${this.dataset.section}`);
        this._navSyncRaf = null;
        this._lastNavSyncMediaId = null;
        this._suppressScrollNavSync = false;
        this.prependMedia = this.dataset.disablePrepend != 'true';
        if (this.productInfo && Shopify.postLinksRetry) this.productInfo.initShareLinks();
        this.filteringOption = this.dataset.filteringOption;
        this.productSpecificFiltering = this.dataset.productSpecificFiltering === 'true';
        try {
          this.optionValues = JSON.parse(this.dataset.optionValues || '[]');
        } catch (e) {
          this.optionValues = [];
        }
        const viewerSlider = this.elements.viewer?.slider;
        if (viewerSlider) {
          viewerSlider.addEventListener('scroll', () => this._queueNavSyncFromScroll(), { passive: true });
          viewerSlider.addEventListener('scrollend', () => this.syncNavFromScroll());
          this._setupMobileViewerLoop();
        }

        this.elements.viewer?.addEventListener('slideChanged', (event) => {
          if (this.mobileMql.matches) return;
          const el = event.detail?.currentElement;
          const mediaId = el && (el.dataset?.mediaId || el.getAttribute('data-media-id'));
          if (mediaId) this.updateGalleryNavForMediaId(mediaId, { force: true });
        });

        if (this.filteringOption) {
          document.addEventListener('variant:change', this.handleVariantChange.bind(this));
          const runInitialFiltering = () => {
            const initialVariant = this.getInitialVariant();
            if (initialVariant) {
              if (this.filteringOption === 'variant_id' || initialVariant[this.filteringOption] != null) {
                this.updateMediaFiltering(initialVariant);
              }
            }
            if (window.MAISON_SELECT_THIRD_THUMBNAIL === true) {
              setTimeout(() => this.selectThirdVisibleThumbnail(), 0);
            }
          };
          if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', runInitialFiltering);
          } else {
            setTimeout(runInitialFiltering, 0);
          }
        } else if (window.MAISON_SELECT_THIRD_THUMBNAIL === true) {
          const runThird = () => setTimeout(() => this.selectThirdVisibleThumbnail(), 0);
          if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', runThird);
          } else {
            runThird();
          }
        }

        if (!this.elements.thumbnails && !this.elements.dots) return;

        // Add thumbnail event listeners
        if (this.elements.thumbnails) {
          this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
            mediaToSwitch
              .querySelector('button')
              .addEventListener('click', (event) => {
                // First set the active media
                this.setActiveMedia(mediaToSwitch.dataset.target, false);
                // Then handle thumbnail scrolling behavior
                this.handleThumbnailClick(event, mediaToSwitch);
              });
          });
        }

        // Add dot navigation event listeners
        if (this.elements.dots) {
          this.elements.dots.querySelectorAll('[data-target]').forEach((dot) => {
            dot.addEventListener('click', () => {
              this.setActiveMedia(dot.dataset.target, false);
            });
          });
        }
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      getInitialVariant() {
        const sectionId = this.dataset.section;
        const form = document.querySelector(`#product-form-${sectionId}`) || this.closest('form') || (this.productInfo && this.productInfo.querySelector('form'));
        const idInput = form && form.querySelector('input[name="id"]');
        if (!idInput || idInput.value === '') return null;
        const id = Number(idInput.value);
        if (!id) return null;
        if (this.filteringOption === 'variant_id') return { id };
        const option = this.filteringOption;
        const optionNum = option.replace('option', '');
        const optionInput = form && (form.querySelector(`input[name="options[${optionNum}]"]`) || form.querySelector(`[data-option="${option}"]`) || form.querySelector(`[name="options[${optionNum}]"]`));
        const optionValue = optionInput ? (optionInput.value || optionInput.getAttribute('value') || (optionInput.selectedOptions && optionInput.selectedOptions[0] && optionInput.selectedOptions[0].value)) : null;
        return { id, [option]: optionValue };
      }

      _queueNavSyncFromScroll() {
        if (!this.mobileMql.matches) return;
        if (this._navSyncRaf) cancelAnimationFrame(this._navSyncRaf);
        this._navSyncRaf = requestAnimationFrame(() => {
          this._navSyncRaf = null;
          this.syncNavFromScroll();
        });
      }

      updateGalleryNavForMediaId(mediaId, { force = false } = {}) {
        if (!mediaId) return;
        if (!force && mediaId === this._lastNavSyncMediaId) return;
        this._lastNavSyncMediaId = mediaId;

        if (this.elements.viewer) {
          this.elements.viewer.querySelectorAll('.product__media-item:not(.hidden)').forEach((el) => {
            const id = el.dataset?.mediaId || el.getAttribute('data-media-id');
            el.classList.toggle('is-active', id === mediaId);
          });
        }
        if (this.elements.thumbnails) {
          const thumb = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
          if (thumb && !thumb.classList.contains('hidden')) {
            this.setActiveThumbnail(thumb);
          }
        }
        if (this.elements.dots) {
          this.setActiveDot(mediaId);
        }
      }

      _visibleMediaSlides() {
        const slider = this.elements.viewer?.slider;
        if (!slider) return [];
        return Array.from(slider.querySelectorAll('.product__media-item')).filter(
          (el) => !el.classList.contains('hidden')
        );
      }

      syncNavFromScroll() {
        if (this._suppressScrollNavSync) return;
        if (!this.mobileMql.matches || !this.elements.viewer?.slider) return;
        const slider = this.elements.viewer.slider;
        const slides = this._visibleMediaSlides();
        if (!slides.length) return;

        const viewLeft = slider.scrollLeft;
        const viewRight = viewLeft + slider.clientWidth;
        let best = slides[0];
        let bestOverlap = -1;
        for (const slide of slides) {
          const left = slide.offsetLeft;
          const right = left + slide.clientWidth;
          const overlap = Math.min(right, viewRight) - Math.max(left, viewLeft);
          if (overlap > bestOverlap) {
            bestOverlap = overlap;
            best = slide;
          } else if (overlap === bestOverlap && overlap > 0 && left < best.offsetLeft) {
            best = slide;
          }
        }
        if (bestOverlap <= 0) {
          let nearest = slides[0];
          let bestDist = Infinity;
          for (const slide of slides) {
            const d = Math.abs(slide.offsetLeft - viewLeft);
            if (d < bestDist) {
              bestDist = d;
              nearest = slide;
            }
          }
          best = nearest;
        }

        const mediaId = best.dataset?.mediaId || best.getAttribute('data-media-id');
        if (!mediaId || mediaId === this._lastNavSyncMediaId) return;
        this.updateGalleryNavForMediaId(mediaId, { force: true });
      }

      _setupMobileViewerLoop() {
        const viewer = this.elements.viewer;
        if (!viewer) return;
        const attach = () => {
          const nextBtn = viewer.querySelector('.slider-button--next');
          const prevBtn = viewer.querySelector('.slider-button--prev');
          if (!nextBtn || !prevBtn || nextBtn.dataset.maisonInfiniteBound === 'true') return;
          nextBtn.dataset.maisonInfiniteBound = 'true';
          prevBtn.dataset.maisonInfiniteBound = 'true';
          nextBtn.addEventListener('click', this._handleMobileNextInfinite.bind(this), true);
          prevBtn.addEventListener('click', this._handleMobilePrevInfinite.bind(this), true);
        };
        attach();
        this.mobileMql.addEventListener('change', attach);
      }

      _handleMobileNextInfinite(event) {
        if (!this.mobileMql.matches) return;
        const slider = this.elements.viewer?.slider;
        if (!slider) return;
        const slides = this._visibleMediaSlides();
        if (slides.length < 2) return;
        const tol = 6;
        const maxScroll = Math.max(0, slider.scrollWidth - slider.clientWidth);
        if (maxScroll <= 0) return;
        if (slider.scrollLeft < maxScroll - tol) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        this._lastNavSyncMediaId = null;
        slider.scrollTo({ left: 0, behavior: 'smooth' });
      }

      _handleMobilePrevInfinite(event) {
        if (!this.mobileMql.matches) return;
        const slider = this.elements.viewer?.slider;
        if (!slider) return;
        const slides = this._visibleMediaSlides();
        if (slides.length < 2) return;
        const tol = 6;
        if (slider.scrollLeft > tol) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        this._lastNavSyncMediaId = null;
        const last = slides[slides.length - 1];
        slider.scrollTo({ left: last.offsetLeft, behavior: 'smooth' });
      }

      setActiveMedia(mediaId, prepend, filtering = false, currentVariant = null) {
        if (filtering && currentVariant) {
          // Use the dedicated filtering method
          this.updateMediaFiltering(currentVariant);
        }

        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');
        this._lastNavSyncMediaId = mediaId;

        if (prepend && this.prependMedia) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          if (!this.section?.settings?.no_scroll) {
            window.scrollTo({ top: top, behavior: 'smooth' });
          }
        });
        this.playActiveMedia(activeMedia);

        if (filtering && currentVariant) {
          if (this.elements.viewer && this.elements.viewer.initPages) this.elements.viewer.initPages();
          if (this.elements.viewer && this.elements.viewer.update) this.elements.viewer.update();
          
          // Update thumbnail navigation after filtering
          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            if (activeThumbnail && !activeThumbnail.classList.contains('hidden')) {
              this.setActiveThumbnail(activeThumbnail);
              this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
            }
          }
          
          // Update dot navigation after filtering
          if (this.elements.dots) {
            this.setActiveDot(mediaId);
          }
          
          return; // Skip the regular navigation update when filtering
        }

        if (!this.elements.thumbnails && !this.elements.dots) return;
        
        // Update thumbnail navigation (regular flow)
        if (this.elements.thumbnails) {
          const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
          this.setActiveThumbnail(activeThumbnail);
          this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
        }
        
        // Update dot navigation (regular flow)
        if (this.elements.dots) {
          this.setActiveDot(mediaId);
        }
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        
        // Check if thumbnail is visible, if not, scroll to make it visible
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        // Get thumbnail position relative to the slider
        const thumbnailRect = thumbnail.getBoundingClientRect();
        const sliderRect = this.elements.thumbnails.slider.getBoundingClientRect();
        
        // Calculate if thumbnail is on the left or right side of the visible area
        const isLeftSide = thumbnailRect.left < sliderRect.left + (sliderRect.width * 0.3);
        const isRightSide = thumbnailRect.right > sliderRect.right - (sliderRect.width * 0.3);
        
        if (isLeftSide) {
          // Scroll to show previous thumbnails (scroll left)
          const scrollAmount = Math.max(0, this.elements.thumbnails.slider.scrollLeft - (this.elements.thumbnails.sliderItemOffset * 2));
          this.elements.thumbnails.slider.scrollTo({ left: scrollAmount, behavior: 'smooth' });
        } else if (isRightSide) {
          // Scroll to show next thumbnails (scroll right)
          const scrollAmount = this.elements.thumbnails.slider.scrollLeft + (this.elements.thumbnails.sliderItemOffset * 2);
          this.elements.thumbnails.slider.scrollTo({ left: scrollAmount, behavior: 'smooth' });
        } else {
          // Default behavior - scroll to make thumbnail visible
          this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft, behavior: 'smooth' });
        }
      }

      setActiveDot(mediaId) {
        if (!this.elements.dots) return;

        this.elements.dots
          .querySelectorAll('.dot-navigation-dot')
          .forEach((dot) => {
            dot.classList.remove('active');
            dot.removeAttribute('aria-current');
          });
        
        const activeDot = this.elements.dots.querySelector(`[data-target="${mediaId}"]`);
        if (activeDot) {
          activeDot.classList.add('active');
          activeDot.setAttribute('aria-current', true);
        }
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
       // window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }

      /**
       * Move a slide (and matching thumbnail + dot) to the start of the DOM so SliderComponent
       * scroll math and counters match visual order. CSS flex `order` alone breaks the slider.
       */
      promoteMediaToFirst(mediaId) {
        if (!mediaId || !this.elements.viewer) return;

        if (this.elements.thumbnails) {
          this.elements.thumbnails.querySelectorAll('.thumbnail-list__item').forEach((li) => {
            li.style.removeProperty('order');
          });
        }

        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) return;

        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((el) => el.classList.remove('is-active'));
        activeMedia.classList.add('is-active');

        const mediaList = activeMedia.parentElement;
        if (mediaList && mediaList.firstElementChild !== activeMedia) {
          mediaList.prepend(activeMedia);
        }

        if (this.elements.thumbnails) {
          const thumb = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
          const thumbList = thumb && thumb.parentElement;
          if (thumbList && thumbList.firstElementChild !== thumb) {
            thumbList.prepend(thumb);
          }
        }

        if (this.elements.dots) {
          const container = this.elements.dots.querySelector('.dot-navigation-container');
          const dot = container && container.querySelector(`[data-target="${mediaId}"]`);
          if (dot && container && container.firstElementChild !== dot) {
            container.prepend(dot);
          }
        }

        if (this.elements.viewer.resetPages) this.elements.viewer.resetPages();
        if (this.elements.thumbnails && this.elements.thumbnails.resetPages) {
          this.elements.thumbnails.resetPages();
        }

        requestAnimationFrame(() => {
          if (mediaList) {
            mediaList.scrollTo({ left: 0, behavior: 'auto' });
          }
        });

        this.playActiveMedia(activeMedia);

        if (this.elements.thumbnails) {
          const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
          if (activeThumbnail) {
            this.setActiveThumbnail(activeThumbnail);
            this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
          }
        }
        if (this.elements.dots) {
          this.setActiveDot(mediaId);
        }
        this._lastNavSyncMediaId = mediaId;
      }

      handleThumbnailClick(event, thumbnail) {
        if (!this.elements.thumbnails || !this.elements.thumbnails.slider) return;
        
        // Get thumbnail position relative to the slider
        const thumbnailRect = thumbnail.getBoundingClientRect();
        const sliderRect = this.elements.thumbnails.slider.getBoundingClientRect();
        
        // Calculate if thumbnail is on the left or right side of the visible area
        const isLeftSide = thumbnailRect.left < sliderRect.left + (sliderRect.width * 0.3);
        const isRightSide = thumbnailRect.right > sliderRect.right - (sliderRect.width * 0.3);
        
        if (isLeftSide) {
          // Scroll to show previous thumbnails (scroll left)
          const scrollAmount = Math.max(0, this.elements.thumbnails.slider.scrollLeft - (this.elements.thumbnails.sliderItemOffset * 2));
          this.elements.thumbnails.slider.scrollTo({ left: scrollAmount, behavior: 'smooth' });
        } else if (isRightSide) {
          // Scroll to show next thumbnails (scroll right)
          const scrollAmount = this.elements.thumbnails.slider.scrollLeft + (this.elements.thumbnails.sliderItemOffset * 2);
          this.elements.thumbnails.slider.scrollTo({ left: scrollAmount, behavior: 'smooth' });
        }
      }

      selectThirdVisibleThumbnail(retries = 6) {
        let mediaId = null;

        if (this.elements.thumbnails) {
          const visibleThumbs = Array.from(
            this.elements.thumbnails.querySelectorAll('.thumbnail-list__item:not(.hidden)')
          );
          if (visibleThumbs.length >= 3) {
            mediaId = visibleThumbs[2].dataset.target;
          }
        }

        if (!mediaId) {
          const visibleSlides = Array.from(this.querySelectorAll('.product__media-item:not(.hidden)'));
          if (visibleSlides.length >= 3) {
            mediaId = visibleSlides[2].dataset.mediaId;
          }
        }

        if (!mediaId) {
          if (retries > 0) setTimeout(() => this.selectThirdVisibleThumbnail(retries - 1), 100);
          return;
        }

        this.promoteMediaToFirst(mediaId);
      }

      handleVariantChange(event) {
        if (!this.filteringOption || !event.detail.variant) return;
        
        // Get the current variant from the event
        const currentVariant = event.detail.variant;
        
        // Update the filtering based on the new variant
        this.updateMediaFiltering(currentVariant);
        
        // Find the first visible media item and set it as active
        const firstVisibleMedia = this.querySelector('.product__media-item:not(.hidden)');
        if (firstVisibleMedia) {
          this.setActiveMedia(firstVisibleMedia.dataset.mediaId, false, true, currentVariant);
        }

        if (window.MAISON_SELECT_THIRD_THUMBNAIL === true) this.selectThirdVisibleThumbnail();
      }

      updateMediaFiltering(currentVariant) {
        if (!this.filteringOption || !currentVariant) return;
        this._lastNavSyncMediaId = null;

        // variant_id: use "id" (or "variant_id" from some payloads)
        const filterValue = this.filteringOption === 'variant_id'
          ? String(currentVariant.id ?? currentVariant.variant_id ?? '')
          : currentVariant[this.filteringOption];

        const matchesFilter = (alt) => {
          if (alt === 'always_display' || !alt || alt === '') return true;
          if (filterValue != null && String(alt) === String(filterValue)) return true;
          if (Array.isArray(this.optionValues) && this.filteringOption !== 'variant_id') {
            const optionIndex = this.optionValues.indexOf(filterValue);
            if (optionIndex >= 0 && alt === String(optionIndex)) return true;
          }
          return false;
        };

        // Filter all media items, thumbnails, and dots
        const allSlides = this.querySelectorAll('.product__media-item, .thumbnail-list__item, .dot-navigation-dot');
        allSlides.forEach(slide => {
          const shouldShow = matchesFilter(slide.dataset.alt);
          if (shouldShow) {
            slide.classList.remove('hidden');
          } else {
            slide.classList.add('hidden');
          }
        });

        // Also filter slider counter links if they exist
        const sliderLinks = this.querySelectorAll('.slider-counter__link');
        sliderLinks.forEach(link => {
          const shouldShow = matchesFilter(link.dataset.alt);
          if (shouldShow) {
            link.classList.remove('hidden');
          } else {
            link.classList.add('hidden');
          }
        });
      }
    }
  );
}
